import { Sparkles } from 'lucide-react';
import { LENS_ORDER, WORKSPACE_LAYOUT, type LensKey } from './constants';
import type { GenerationBrief } from '@/services/api';

interface LensTabsProps {
  activeLens: LensKey;
  onChange: (lens: LensKey) => void;
  brief: GenerationBrief | null;
  /** Count of ad_generation runs for this brief (not in playbook_ids). */
  briefAdPackJobCount?: number;
}

export function LensTabs({ activeLens, onChange, brief, briefAdPackJobCount = 0 }: LensTabsProps) {
  return (
    <div
      className="sticky top-16 z-10 bg-white border-b border-slate-200 px-6 flex items-end gap-1"
      style={{ height: WORKSPACE_LAYOUT.LENS_TAB_HEIGHT }}
    >
      {LENS_ORDER.map(({ key, label }) => {
        const isActive = key === activeLens;
        const count = lensCount(key, brief, briefAdPackJobCount);
        const isStrategy = key === 'strategy';
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            className={`relative px-3 h-full text-[12px] font-medium transition border-b-2 ${
              isActive
                ? isStrategy
                  ? 'text-violet-700 border-violet-500'
                  : 'text-slate-900 border-brand-purple'
                : 'text-slate-500 hover:text-slate-700 border-transparent'
            }`}
          >
            <span className="flex items-center gap-1.5">
              {isStrategy && (
                <Sparkles
                  size={11}
                  className={isActive ? 'text-violet-500' : 'text-slate-400'}
                />
              )}
              {label}
              {count !== null && (
                <span
                  className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                    isActive
                      ? 'bg-brand-purple/10 text-brand-purple'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {count}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function lensCount(
  lens: LensKey,
  brief: GenerationBrief | null,
  briefAdPackJobCount: number,
): number | null {
  if (!brief) return null;

  if (lens === 'products') {
    const cfg = brief.product_config;
    if (!cfg) return null;
    // Resolved set is authoritative — show it regardless of mode.
    const resolved = cfg.resolved_product_ids?.length ?? 0;
    if (resolved > 0) return resolved;
    // Explicit: count hand-picked IDs.
    if (cfg.mode === 'explicit') return (cfg.product_ids?.length ?? 0) || null;
    // Top-N: reflect the configured target count.
    if (cfg.mode === 'top_n' && cfg.top_n) return cfg.top_n;
    return null;
  }

  if (lens === 'angles') {
    const all = brief.selected_angles ?? [];
    if (all.length === 0) return null;
    return all.filter((a) => a.status === 'approved').length;
  }

  if (lens === 'assets') {
    const n = (brief.playbook_ids?.length ?? 0) + briefAdPackJobCount;
    return n > 0 ? n : null;
  }

  if (lens === 'performance') {
    const n = (brief.playbook_ids?.length ?? 0) + briefAdPackJobCount;
    return n > 0 ? n : null;
  }

  if (lens === 'connected_ads') {
    // Approximate without an extra fetch: source_creative_ids is the
    // canonical set for strategic_rebuild briefs (the actual count
    // includes direct + playbook linkage too, but those are typically
    // a superset; the badge gives a useful at-a-glance signal).
    const n = brief.source_creative_ids?.length ?? 0;
    return n > 0 ? n : null;
  }

  return null;
}
